import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseAuthQueryParamOptions {
  /** Whether the user already has an API key (skip opening modals if so) */
  hasApiKey: boolean
  /** Callback when ?auth=signup is detected */
  onSignUpOpen: () => void
  /** Callback when ?auth=signin is detected */
  onSignInOpen: () => void
}

/**
 * Reads the `?auth=signup` or `?auth=signin` query parameter and opens the
 * corresponding modal. The parameter is consumed (removed) immediately so
 * a page refresh won't re-trigger the modal.
 *
 * Multiple pages link to `/editor?auth=signup` — this hook keeps that
 * behaviour in one place.
 */
export function useAuthQueryParam({
  hasApiKey,
  onSignUpOpen,
  onSignInOpen,
}: UseAuthQueryParamOptions) {
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const authParam = searchParams.get('auth')
    if (authParam && !hasApiKey) {
      if (authParam === 'signup') {
        onSignUpOpen()
      } else if (authParam === 'signin') {
        onSignInOpen()
      }
      // Clear the param
      searchParams.delete('auth')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, hasApiKey, onSignUpOpen, onSignInOpen])
}
